export const MIN_CROP_ZOOM = 1;
export const MAX_CROP_ZOOM = 3;

export type CropPan = { x: number; y: number };

export type CropSize = { width: number; height: number };

export type NaturalCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Display size that covers the frame, then scaled by `zoom`. */
export function coverSize(
  frameW: number,
  frameH: number,
  naturalW: number,
  naturalH: number,
  zoom: number,
): CropSize {
  if (
    frameW <= 0 ||
    frameH <= 0 ||
    naturalW <= 0 ||
    naturalH <= 0 ||
    zoom <= 0
  ) {
    return { width: 0, height: 0 };
  }

  const base = Math.max(frameW / naturalW, frameH / naturalH);
  return {
    width: naturalW * base * zoom,
    height: naturalH * base * zoom,
  };
}

export function clampPan(
  pan: CropPan,
  frameW: number,
  frameH: number,
  displayW: number,
  displayH: number,
): CropPan {
  const minX = Math.min(0, frameW - displayW);
  const minY = Math.min(0, frameH - displayH);
  return {
    x: Math.min(0, Math.max(minX, pan.x)),
    y: Math.min(0, Math.max(minY, pan.y)),
  };
}

export function centerPan(
  frameW: number,
  frameH: number,
  displayW: number,
  displayH: number,
): CropPan {
  return clampPan(
    {
      x: (frameW - displayW) / 2,
      y: (frameH - displayH) / 2,
    },
    frameW,
    frameH,
    displayW,
    displayH,
  );
}

/** Keep the frame point `origin` over the same image pixel while zooming. */
export function zoomAround(
  pan: CropPan,
  zoom: number,
  nextZoom: number,
  frameW: number,
  frameH: number,
  naturalW: number,
  naturalH: number,
  originX: number,
  originY: number,
): CropPan {
  const prev = coverSize(frameW, frameH, naturalW, naturalH, zoom);
  const next = coverSize(frameW, frameH, naturalW, naturalH, nextZoom);
  if (prev.width === 0 || prev.height === 0) {
    return centerPan(frameW, frameH, next.width, next.height);
  }

  const imageX = (originX - pan.x) / prev.width;
  const imageY = (originY - pan.y) / prev.height;
  return clampPan(
    {
      x: originX - imageX * next.width,
      y: originY - imageY * next.height,
    },
    frameW,
    frameH,
    next.width,
    next.height,
  );
}

/** Source rectangle in natural image pixels for the visible frame. */
export function naturalCropRect(
  pan: CropPan,
  zoom: number,
  frameW: number,
  frameH: number,
  naturalW: number,
  naturalH: number,
): NaturalCropRect {
  const display = coverSize(frameW, frameH, naturalW, naturalH, zoom);
  if (display.width === 0 || display.height === 0) {
    return { x: 0, y: 0, width: naturalW, height: naturalH };
  }

  const scaleX = naturalW / display.width;
  const scaleY = naturalH / display.height;
  let x = -pan.x * scaleX;
  let y = -pan.y * scaleY;
  let width = frameW * scaleX;
  let height = frameH * scaleY;

  x = Math.min(Math.max(0, x), naturalW);
  y = Math.min(Math.max(0, y), naturalH);
  width = Math.min(width, naturalW - x);
  height = Math.min(height, naturalH - y);

  return {
    x,
    y,
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
}
